/**
 * lifecycleHooks — LUẬT THUẦN cho bộ SCRIPT VÒNG ĐỜI của DataListManager.
 *
 * Bù nốt khoảng trống cuối so với InputBase: `on_init`, `on_init_update`, `on_saved`, `on_deleting`,
 * `on_deleted`, `handle_cell_clicked` (và `expression_check_save` đã có từ đợt trước).
 *
 * ĐIỂM MẤU CHỐT — "thứ tự tham số" thực ra là "TÊN được gán".
 * Script được biên dịch bằng `new Function(...tenThamSo, than_script)`, nên thân script gọi tham số theo
 * TÊN chứ không theo vị trí. Vì vậy ta gán ĐỦ CẢ HAI bộ tên: bộ của InputBase (`master`, `detail`, `API`,
 * `utils`…) để script cũ dán sang chạy nguyên si, VÀ bộ của DataListManager (`formData`, `masterData`,
 * `fields`) để viết mới cho nhất quán. Không phải chọn một trong hai, và không tốn gì.
 *
 * File KHÔNG import gì → test node nạp và chạy được trên chính mã nguồn này.
 */

/**
 * Tên tham số của từng hook, theo ĐÚNG thứ tự InputBase rồi mới đến phần thêm.
 * Thứ tự chỉ còn ý nghĩa cho người đọc; điều thật sự quan trọng là ĐỦ TÊN.
 */
const HOOK_ARGS = {
	// expression_check_save — InputBase: (master, detail, API, context, utils, parent)
	checkBeforeSave: ['master', 'detail', 'API', 'context', 'utils', 'parent',
		'fields', 'formData', 'masterData'],
	// on_saved — InputBase: (master, detail, origin_data, API, context, utils, action, parent)
	onSaved: ['master', 'detail', 'origin_data', 'API', 'context', 'utils', 'action', 'parent',
		'fields', 'formData', 'masterData', 'originData'],
	// on_deleting — InputBase: (master, detail, API, context, utils)
	onDeleting: ['master', 'detail', 'API', 'context', 'utils',
		'fields', 'formData', 'masterData'],
	// on_deleted — InputBase: (master, rows, detail, API, context, utils)
	onDeleted: ['master', 'rows', 'detail', 'API', 'context', 'utils',
		'fields', 'formData', 'masterData'],
	// on_init — InputBase: (master, detail, API, context, utils, mediaQueryMatches, action)
	onInit: ['master', 'detail', 'API', 'context', 'utils', 'mediaQueryMatches', 'action',
		'fields', 'formData', 'masterData'],
	// on_init_update — InputBase: (master, detail, API, context, utils, mediaQueryMatches)
	onInitUpdate: ['master', 'detail', 'API', 'context', 'utils', 'mediaQueryMatches',
		'fields', 'formData', 'masterData'],
	// handle_cell_clicked — InputBase: (row, column, module, context, history)
	handleCellClicked: ['row', 'column', 'module', 'context', 'history', 'fields'],
};

/**
 * Hook nào CHẶN được hành động, và chặn bằng cách nào.
 *   error   — trả `{ error }` thì chặn (mọi hook chặn được đều có)
 *   string  — trả chuỗi khác rỗng cũng là thông báo chặn
 *   false   — trả đúng `false` thì chặn
 *
 * `onDeleting` cố ý nhận CẢ chuỗi, khác InputBase (chỉ xét `rs.error`): với thao tác xoá thì chặn nhầm
 * chỉ gây phiền, còn KHÔNG chặn được là mất dữ liệu.
 */
const HOOK_BLOCKING = {
	checkBeforeSave: { error: true, string: true, false: false },
	onDeleting: { error: true, string: true, false: false },
	onInit: { error: true, string: false, false: true },
	onInitUpdate: { error: true, string: false, false: false },
	handleCellClicked: { error: true, string: false, false: true },
	onSaved: null,     // đã lưu xong rồi, không chặn được gì
	onDeleted: null,   // đã xoá xong rồi
};

/** Hook có tồn tại và có luật chặn không. */
function canBlock(hookName) {
	return !!HOOK_BLOCKING[hookName];
}

/**
 * Diễn giải giá trị hook trả về.
 * @returns {{blocked: boolean, message: string|null}} — `message` là chuỗi để hiện cho người dùng
 *          (có thể null khi chặn im lặng, vd script trả `false`).
 */
function interpretHookResult(hookName, rs) {
	const rule = HOOK_BLOCKING[hookName];
	if (!rule) return { blocked: false, message: null };

	// `false` là cách dừng IM LẶNG của onInit / handleCellClicked (script đã tự điều hướng rồi).
	// Phải xét riêng vì nó không phải object cũng không phải chuỗi — `!!false` mà rơi xuống nhánh
	// mặc định thì thành "cho qua".
	if (rs === false) return rule.false ? { blocked: true, message: null } : { blocked: false, message: null };
	if (rs === undefined || rs === null || rs === true) return { blocked: false, message: null };

	if (typeof rs === 'string') {
		if (!rule.string || !rs.trim()) return { blocked: false, message: null };
		return { blocked: true, message: rs };
	}

	if (typeof rs === 'object') {
		const msg = rs.error || rs.message;
		if (msg === undefined || msg === null || msg === false) return { blocked: false, message: null };
		// `{error: true}` là chặn nhưng không có lời nào để hiện.
		if (msg === true) return { blocked: true, message: null };
		const text = typeof msg === 'string' ? msg : String(msg);
		if (!text.trim()) return { blocked: false, message: null };
		return { blocked: true, message: text };
	}

	return { blocked: false, message: null };
}

/**
 * Dựng MẢNG tham số truyền vào hook, theo đúng `HOOK_ARGS[hookName]`.
 * `bag` là túi giá trị đặt theo tên; các tên đồng nghĩa được nối sẵn ở đây để người gọi khỏi lặp lại.
 */
function buildHookArgs(hookName, bag) {
	const names = HOOK_ARGS[hookName];
	if (!names) return [];
	const b = bag || {};
	// Đồng nghĩa: bộ tên DataListManager ánh xạ sang bộ tên InputBase.
	const resolved = {
		...b,
		formData: b.formData !== undefined ? b.formData : b.detail,
		masterData: b.masterData !== undefined ? b.masterData : b.master,
		originData: b.originData !== undefined ? b.originData : b.origin_data,
		origin_data: b.origin_data !== undefined ? b.origin_data : b.originData,
	};
	return names.map(n => resolved[n]);
}

/**
 * KHÁC INPUTBASE — có chủ ý: InputBase cho `on_deleted` trả `{ rows }` để thay danh sách đang hiển thị.
 * Ở đây danh sách do react-query giữ và được nạp LẠI TỪ SERVER ngay sau khi xoá, nên giá trị đó sẽ bị
 * ghi đè trong tích tắc. Thà không có còn hơn có mà im lặng không ăn thua. `rows` vẫn được truyền vào
 * làm THAM SỐ (danh sách còn lại) để script đọc.
 */

/**
 * Ghi chú về `location`: InputBase phải bỏ bớt tham số `location` khi thân script có nhắc
 * `location.href`, để tên đó rơi về `window.location`. Ở đây KHÔNG gán `location` cho hook nào cả, nên
 * `location` trong script luôn là `window.location` — cùng kết quả mà không cần mẹo dò chuỗi.
 */

/**
 * =====================================================================
 * `handle_value_changed` — SCRIPT CHẠY KHI MỘT FIELD ĐỔI GIÁ TRỊ
 * ---------------------------------------------------------------------
 * Nguồn chuẩn: `GroupTabs.handleValueChanged` (`GroupTabs.js:110-153`):
 *
 *   new Function("master","detail","data","old_data","value","ref_value",
 *                "utils","API","context","moment","numeral", body)
 *   func(this.props.master || _data, _data, _data, old_data, _value, ref_value||{},
 *        utils, this.context.apis, this.context, moment, numeral)
 *
 * BỐN điều dễ dịch sai, mỗi cái đều làm script chạy im lặng mà sai:
 *
 * 1. **Script SỬA THẲNG vào object, không `return`.** Ví dụ thật:
 *    `master.ty_gia = ref_value.ty_gia||1;` — không có `return` nào. Bên gọi PHẢI lấy lại thay đổi từ
 *    chính object đã truyền vào, chứ không chỉ trộn giá trị trả về.
 * 2. **`detail` và `data` là CÙNG MỘT object** (`_data`) — bản ghi hiện tại ĐÃ có giá trị mới.
 * 3. **`value` là một OBJECT `{ [tên field]: giá trị mới }`**, không phải giá trị trần
 *    (`GroupTabs.js:112` đọc `value[field.name]`). Gán giá trị trần vào là script cũ đọc `value.ma_nt`
 *    ra `undefined`.
 * 4. **`ref_value` là BẢN GHI ĐẦY ĐỦ** của danh mục tham chiếu (`ref_data_fields` quyết định có những
 *    cột nào), không phải cặp `{value,label}`. Ở bộ mới nó nằm trong `optionObj.raw`. Luôn có mặt (mặc
 *    định `{}`) để `ref_value.ty_gia` không ném khi người dùng xoá trắng ô.
 *
 * Thứ tự dưới đây theo InputBase trước, phần thêm của DataListManager sau — nhưng như cả file này,
 * cái quan trọng là ĐỦ TÊN.
 */
const VALUE_CHANGED_ARGS = [
	'master', 'detail', 'data', 'old_data', 'value', 'ref_value',
	'utils', 'API', 'context', 'moment', 'numeral',
	'newValue', 'oldValue', 'formData', 'optionObj', 'masterData', 'fields',
];

/** Bộ tên CŨ của `onValueChanged` — dùng làm phương án lùi khi biên dịch với bộ đầy đủ bị lỗi. */
const VALUE_CHANGED_ARGS_LEGACY = ['newValue', 'oldValue', 'formData', 'optionObj', 'masterData', 'fields', 'context'];

/**
 * Dựng mảng giá trị cho `VALUE_CHANGED_ARGS`.
 *
 * @param bag { field, newValue, oldValue, formData, master, optionObj, fields, context, utils, moment, numeral }
 *            `formData` là object SẼ BỊ SỬA TẠI CHỖ bởi script — truyền đúng object mà bên gọi định lưu.
 */
function buildValueChangedArgs(bag) {
	const b = bag || {};
	const formData = b.formData || {};
	// `master` không có (form không nằm trong form nào) thì CHÍNH nó là master — đúng
	// `this.props.master || _data` của bộ cũ.
	const master = b.master || formData;
	const ref = refValueFrom(b.optionObj);
	const resolved = {
		master, masterData: master,
		detail: formData, data: formData, formData,
		old_data: b.oldData || {},
		value: b.field ? { [b.field]: b.newValue } : {},
		ref_value: ref, optionObj: b.optionObj,
		newValue: b.newValue, oldValue: b.oldValue,
		utils: b.utils || null, API: (b.context || {}).apis || null, context: b.context || {},
		moment: b.moment || null, numeral: b.numeral || null,
		fields: b.fields || [],
	};
	return VALUE_CHANGED_ARGS.map(n => resolved[n]);
}

/** `optionObj` của bộ mới -> `ref_value` của bộ cũ: BẢN GHI GỐC, không phải cặp {value,label}. */
function refValueFrom(optionObj) {
	if (!optionObj) return {};
	if (Array.isArray(optionObj)) return optionObj.map(o => (o && o.raw) || o || {});
	return optionObj.raw || optionObj;
}

/**
 * Những khoá CẤP MỘT mà script đã sửa trên `draft` so với `origin`.
 *
 * Dùng cho dòng chi tiết: script sửa `master.tien_hang` nhưng `master` ở đó là bản ghi MẸ nằm ở state
 * của form cha — sửa tại chỗ thì cha không vẽ lại. Nên chạy script trên một BẢN SAO rồi gửi phần chênh
 * lệch lên cho cha.
 *
 * CHỈ so sánh CẤP MỘT: sửa lồng (`master.a.b = 1`) đụng vào chính object dùng chung nên vốn đã "ăn"
 * ngay, nhưng React không biết mà vẽ lại — đó là giới hạn đã biết, không phải chỗ để đoán thêm.
 */
function masterPatchFrom(draft, origin) {
	const patch = {};
	if (!draft || typeof draft !== 'object') return patch;
	const base = origin || {};
	for (const k in draft) {
		if (!Object.prototype.hasOwnProperty.call(draft, k)) continue;
		if (draft[k] !== base[k]) patch[k] = draft[k];
	}
	return patch;
}

export {
	HOOK_ARGS, HOOK_BLOCKING, canBlock,
	interpretHookResult, buildHookArgs,
	VALUE_CHANGED_ARGS, VALUE_CHANGED_ARGS_LEGACY,
	buildValueChangedArgs, refValueFrom, masterPatchFrom,
};
